import {
  ArrowDropDown,
  Logout,
  Menu as MenuIcon,
  Payments,
  RocketLaunch,
  Subscriptions,
  SupervisedUserCircleSharp,
  VerifiedUser,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { Link, Outlet } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { drawerWidth } from "../config/config";
import { getInitials } from "../utils/StringTool";
import { useDashboard } from "./hooks/useDashboard";

const Dashboard: React.FC = () => {
  const {
    user,
    userData,
    permittedPaths,
    isMobile,
    mobileOpen,
    dialogOpen,
    location,
    theme,
    open,
    anchorEl,
    notifications,
    userMenuOpen,
    userMenuAnchor,
    handleUserMenuOpen,
    handleUserMenuClose,
    handleOpen,
    handleClose,
    setMobileOpen,
    setDialogOpen,
    handleDrawerToggle,
    handleLogout,
  } = useDashboard();

  const menuGroups = [
    // {
    //   title: "Inicio",
    //   items: [
    //     {
    //       text: "Analíticas",
    //       icon: <Analytics />,
    //       path: "/dashboard",
    //     },
    //   ],
    // },
    {
      title: "Inicio",
      items: [
        {
          text: "Mi Kit",
          icon: <RocketLaunch />,
          path: "/dashboard/plan",
        },
      ],
    },
    {
      title: "Gestión",
      items: [
        {
          text: "Usarios",
          icon: <SupervisedUserCircleSharp />,
          path: "/dashboard/admin/users",
        },
        {
          text: "Planes",
          icon: <Subscriptions />,
          path: "/dashboard/admin/plans",
        },
        {
          text: "Pagos",
          icon: <Payments />,
          path: "/dashboard/admin/payments",
        },
      ],
    },
    {
      title: "Mi perfil",
      items: [
        {
          text: "Datos personales",
          icon: <VerifiedUser />,
          path: "/dashboard/profile",
        },
        // {
        //   text: "Administrar familiares",
        //   icon: <ManageAccounts />,
        //   path: "/dashboard/accounts",
        // },
      ],
    },
  ];

  const menuProfile = (
    <Menu
      anchorEl={userMenuAnchor}
      open={userMenuOpen}
      onClose={handleUserMenuClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 220,
          borderRadius: 3,
          boxShadow: 4,
          p: 0,
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar
          src={user?.photoURL || ""}
          alt={userData?.fullName || "Usuario"}
          sx={{
            width: 40,
            height: 40,
            background: theme.palette.secondary.main,
            fontWeight: 700,
          }}
        >
          {" "}
          {!user?.photoURL && getInitials(userData?.fullName)}
        </Avatar>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {userData?.fullName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {user?.email || user?.phoneNumber}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <MenuItem
        onClick={() => {
          handleUserMenuClose();
          setDialogOpen(true);
        }}
        sx={{
          color: "error.main",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ListItemIcon sx={{ minWidth: 30, color: "error.main" }}>
          <Logout fontSize="small" />
        </ListItemIcon>
        Salir
      </MenuItem>
    </Menu>
  );

  const menuNotifications = (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 320,
          maxWidth: 360,
          borderRadius: 3,
          boxShadow: 4,
          p: 0,
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Notificaciones
        </Typography>
      </Box>
      <Divider />

      {notifications.length === 0 ? (
        <MenuItem
          disabled
          sx={{
            py: 3,
            justifyContent: "center",
            color: "text.secondary",
            opacity: 0.8,
            fontSize: 15,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
          >
            <Typography sx={{ color: "text.secondary" }}>
              No tienes notificaciones
            </Typography>
          </Box>
        </MenuItem>
      ) : (
        notifications.map((n) => (
          <MenuItem
            key={n.id}
            sx={{ alignItems: "flex-start", gap: 1.5, py: 1.2, px: 2 }}
            onClick={handleClose}
          >
            <ListItemAvatar sx={{ minWidth: 30 }}>
              <Avatar
                sx={{
                  background: theme.palette.secondary.main,
                  width: 30,
                  height: 30,
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                {!user?.photoURL && getInitials(userData?.fullName)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {n.title}
                </Typography>
              }
              secondary={
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {n.subtitle}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      float: "right",
                      color: "text.disabled",
                      fontWeight: 400,
                      ml: 2,
                    }}
                  >
                    {n.time}
                  </Typography>
                </Box>
              }
              sx={{ my: 0 }}
            />
          </MenuItem>
        ))
      )}
    </Menu>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <List sx={{ flexGrow: 1, fontSize: 8 }}>
        {menuGroups.map((group, idx) => {
          const visibleItems = group.items.filter(({ path }) =>
            permittedPaths.includes(path)
          );
          if (visibleItems.length === 0) return null;
          return (
            <Box key={group.title} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  pt: idx === 0 ? 0 : 3,
                  pb: 0.5,
                  color: "text.primary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontSize: "12px",
                }}
              >
                {group.title}
              </Typography>
              {group.items
                .filter(({ path }) => permittedPaths.includes(path))
                .map(({ text, icon, path }) => (
                  <ListItemButton
                    key={text}
                    component={Link}
                    to={path}
                    onClick={isMobile ? () => setMobileOpen(false) : undefined}
                    selected={location.pathname === path}
                    sx={{
                      borderRadius: "0px",
                      height: "40px",
                      bgcolor:
                        location.pathname === path
                          ? "action.selected"
                          : "transparent",
                      margin: "2px 2px",
                      padding: "12px",
                      borderRight: `3px solid ${
                        location.pathname === path
                          ? theme.palette.primary.main
                          : "transparent"
                      }`,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(icon, {
                        sx: {
                          fontSize: 20,
                          color: "secondary.main",
                        },
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: "14px",
                            fontWeight: "normal",
                            color: "text.primary",
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                ))}
            </Box>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <ConfirmationDialog
        open={dialogOpen}
        title="Confirmar cierre de sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleLogout}
        confirmText="Cerrar Sesión"
      />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="secondary"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {/* <Tooltip title="Notificaciones">
            <IconButton color="inherit" onClick={handleOpen}>
              <Badge badgeContent={notifications.length} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip> */}
          <Box sx={{ width: 10 }} />
          <Tooltip title="Cuenta">
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                px: 1,
                py: 0.5,
                borderRadius: "50px",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Avatar
                src={user?.photoURL || ""}
                alt={userData?.fullName || "Usuario"}
                sx={{
                  width: 30,
                  height: 30,
                  background: theme.palette.secondary.main,
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {!user?.photoURL && getInitials(userData?.fullName)}
              </Avatar>
              <ArrowDropDown sx={{ color: "text.primary" }} />
            </Box>
          </Tooltip>
        </Toolbar>
      </AppBar>
      {menuProfile}
      {menuNotifications}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            py: 4,
          }}
        >
          <Box
            sx={{
              background: "rgba(20, 45, 185, 1)",
              borderRadius: 4,
              p: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/assets/icons/logo.png"
              alt="Convisas Logo"
              style={{
                maxWidth: 110,
                borderRadius: 12,
              }}
            />
          </Box>
        </Box>
        {drawerContent}
      </Drawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: "background.default", p: 0 }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Dashboard;
